// conditional-options.js (robust version)
// Shows the "Metal" option only when "Pendant" is selected.
// Works for selects, radio/swatches, and themes that re-render option DOM.

(function () {
  function debounce(fn, wait = 60) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  document.addEventListener('DOMContentLoaded', initConditionalOptions);

  function initConditionalOptions() {
    const productInfo = document.querySelector('product-info[data-option-names]');
    if (!productInfo) return;

    const inRudraksha = (productInfo.getAttribute('data-in-rudraksha') || 'false') === 'true';
    if (!inRudraksha) return; // only run for Rudraksha collection products

    let optionNames = [];
    try {
      optionNames = JSON.parse(productInfo.getAttribute('data-option-names') || '[]');
    } catch (e) {
      console.warn('conditional-options: failed to parse option names', e);
      return;
    }
    if (!optionNames || optionNames.length === 0) return;

    // keywords
    const pendantKeywords = ['pendant', 'pendent', 'loose/pendent', 'loose/pendant', 'loose / pendent', 'loose'];
    const metalKeywords = ['metal', 'select metal', 'metal type', 'jewelry material'];

    const findOptionIndexByKeywords = (keywords) => {
      const lower = optionNames.map(n => (n || '').toLowerCase());
      for (let i = 0; i < lower.length; i++) {
        for (const kw of keywords) {
          if (lower[i].includes(kw)) return i;
        }
      }
      return -1;
    };

    const pendantIndex = findOptionIndexByKeywords(pendantKeywords);
    const metalIndex = findOptionIndexByKeywords(metalKeywords);

    if (pendantIndex === -1 || metalIndex === -1) {
      console.info('conditional-options: could not identify Pendant or Metal option', { optionNames, pendantIndex, metalIndex });
      return;
    }

    const pendantName = optionNames[pendantIndex];
    const metalName = optionNames[metalIndex];

    // Try to find container for an option by label/text (fallback)
    function findControlForOption(optionName) {
      // 1) look for form controls with name="options[Option Name]"
      const qName = `options[${optionName}]`;
      const selector = `select[name="${qName}"], input[name="${qName}"]`;
      const els = Array.from(productInfo.querySelectorAll(selector));
      if (els.length) return els[0];

      // 2) labels where text matches option name
      const labels = Array.from(productInfo.querySelectorAll('label'));
      for (const lbl of labels) {
        if ((lbl.textContent || '').trim() === optionName) {
          const forAttr = lbl.getAttribute('for');
          if (forAttr) {
            const control = document.getElementById(forAttr);
            if (control) return control;
          }
          const parent = lbl.closest('.product-option, .selector-wrapper, .product-form__input, fieldset, .variant-option');
          if (parent) return parent;
        }
      }

      // 3) fallback: find container that contains the option name
      const candidates = Array.from(productInfo.querySelectorAll('.product-option, .selector-wrapper, .product-form__input, fieldset, .swatch, .selector, .variant-option'));
      const lowered = optionName.toLowerCase();
      for (const c of candidates) {
        if ((c.textContent || '').toLowerCase().includes(lowered)) return c;
      }

      return null;
    }

    // Get the currently selected value for an option name (robust)
    function getOptionValue(optionName) {
      const qName = `options[${optionName}]`;
      // 1) direct selects / inputs
      const selects = Array.from(productInfo.querySelectorAll(`select[name="${qName}"]`));
      if (selects.length) return selects[0].value;

      const inputs = Array.from(productInfo.querySelectorAll(`input[name="${qName}"]`));
      if (inputs.length) {
        const checked = inputs.find(i => i.checked);
        if (checked) return checked.value;
        // if none checked, maybe the theme uses aria-checked on buttons with same name attribute
        const ariaChecked = inputs.find(i => i.getAttribute('aria-checked') === 'true');
        if (ariaChecked) return ariaChecked.value || ariaChecked.getAttribute('data-value') || ariaChecked.textContent;
      }

      // 2) try to find a swatch/radio container and its active child (by aria-checked or selected class)
      const ctrl = findControlForOption(optionName);
      if (ctrl) {
        // radio group inside ctrl
        const checkedInput = ctrl.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
        if (checkedInput) return checkedInput.value;
        const aria = ctrl.querySelector('[aria-checked="true"], .is-active, .active, .selected');
        if (aria) return (aria.getAttribute('data-value') || aria.textContent || '').trim();

        // if ctrl is select element itself
        if (ctrl.tagName === 'SELECT') return ctrl.value;
      }

      // 3) last fallback: search for any element containing the optionName and inside a selected element
      const fallback = productInfo.querySelector(`[data-option-name="${optionName}"]`);
      if (fallback) {
        const inner = fallback.querySelector('[aria-checked="true"], .is-active, .active, .selected, input[type="radio"]:checked');
        if (inner) return inner.value || inner.getAttribute('data-value') || inner.textContent;
      }

      return null;
    }

    // Find current metal wrapper (closest sensible wrapper)
    function findMetalWrapper() {
      const metalControl = findControlForOption(metalName);
      if (!metalControl) return null;
      return metalControl.closest('.product-form__input') || metalControl.closest('.product-option') || metalControl.parentElement || metalControl;
    }

    function isPendantSelected() {
      const val = getOptionValue(pendantName);
      if (!val) return false;
      return /pendant|pendent/i.test(String(val).toLowerCase());
    }

    function updateMetalVisibility() {
      try {
        const metalWrapper = findMetalWrapper();
        if (!metalWrapper) return;
        const show = isPendantSelected();
        if (show) {
          metalWrapper.style.display = '';
          metalWrapper.setAttribute('aria-hidden', 'false');
        } else {
          metalWrapper.style.display = 'none';
          metalWrapper.setAttribute('aria-hidden', 'true');
        }
      } catch (e) {
        console.warn('conditional-options update error', e);
      }
    }

    // Delegated listeners on the productInfo container so we don't rely on specific nodes
    const debouncedUpdate = debounce(updateMetalVisibility, 40);
    productInfo.addEventListener('change', debouncedUpdate);
    productInfo.addEventListener('click', debouncedUpdate);
    productInfo.addEventListener('keyup', debouncedUpdate);

    // Re-evaluate when productInfo DOM changes (themes often replace option DOM)
    const mo = new MutationObserver(debounce(updateMetalVisibility, 80));
    mo.observe(productInfo, { childList: true, subtree: true });

    // Also respond to theme events (if present)
    document.addEventListener('variant:change', debouncedUpdate);
    document.addEventListener('product:variants:changed', debouncedUpdate);

    // initial run
    setTimeout(updateMetalVisibility, 60);
  }
})();
